import { Injectable } from '@angular/core';
import { PhoneNumber } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { formatFileSize, parseFlexiblePhoneNumber } from '../../../utils/utils';
import { v4 as uuidv4 } from 'uuid'; // ← ADD THIS
import { csvFileName, downloadCsv, toCsv } from '../../../utils/csv';

// ... your interface stays the same ...
export interface IPhoneNumbersFromCSV {
  batchId: string; // ← NEW: unique identifier
  fileName: string;
  fileSize: string;
  filePhoneNumbersCount: number;
  submitted: { phoneNumber: string; id: string; country?: string }[];
  duplicated: {
    phoneNumber: string;
    count: number;
    variations?: string[];
    country?: string;
  }[];
  updated: ICsvEdit[];
  removed: { phoneNumber: string }[];
}

/** One number corrected by hand in the upload modal. */
export interface ICsvEdit {
  /** The edited entry's current id (E.164, or a synthetic id for invalid rows). */
  id: string;
  /** The number as it read before the first edit. */
  previous?: string;
  /** The number it reads now. */
  update: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocalCsvService {
  constructor() {}

  // FROM THE FILE / FILE RELATED COMPUTATIONS-----------------------------------------------------------------------------------------------
  private fileName: string = '';
  private fileSize: number = 0;
  private tableHeaders: string[] = [];
  private tableData: string[][] = [];
  private formattedFileData: Record<string, any>[] = [];
  private columnMapping: Record<string, string> = {};

  resetFileRelatedInfo() {
    this.fileName = '';
    this.fileSize = 0;
    this.tableHeaders = [];
    this.tableData = [];
    this.formattedFileData = [];
    this.columnMapping = {};
  }

  get getTableHeaders(): string[] {
    return this.tableHeaders;
  }

  get getTableData(): string[][] {
    return this.tableData;
  }

  updateColumnMapping(mapping: Record<string, string>): void {
    this.columnMapping = { ...mapping };
  }

  applyMappingAndGenerate(): void {
    this.generateFormattedFileData(this.tableHeaders, this.tableData);
  }

  updateFileName(fileName: string) {
    this.fileName = fileName;
  }

  updateFileSize(size: number) {
    this.fileSize = size;
  }

  updateTableHeaders(headers: string[]): void {
    this.tableHeaders = headers;
  }
  updateTableData(data: string[][]): void {
    this.tableData = data;
  }

  get getFileName() {
    return this.fileName;
  }

  get getFileSize() {
    return formatFileSize(this.fileSize);
  }

  get getFormattedFileDataCount() {
    return this.formattedFileData.length;
  }

  generateFormattedFileData(headers: string[], data: string[][]) {
    let result: any[] = [];
    if (this.tableHeaders.length > 0 && this.tableData.length > 0) {
      this.tableData.forEach((row) => {
        let obj: any = {};
        this.tableHeaders.forEach((header, index) => {
          obj[header] = row[index];
        });
        result.push(obj);
      });
    }
    this.formattedFileData = result;

    this.firstUpdateFormattedData();
  }

  //------------------------------------------------------------------------------ WORKSPACE RELATED COMPUTATIONS--------------------------------------------------------------------------------
  private formattedData: {
    phoneNumber: string;
    id: string;
    country?: string;
    isValid?: boolean;
    /** Position among the file's data rows, 1-based (header and blank rows not counted). */
    row?: number;
  }[] = [];
  private duplicates: {
    phoneNumber: string;
    count: number;
    variations?: string[];
    country?: string;
  }[] = [];
  private updated: ICsvEdit[] = [];
  private removed: { phoneNumber: string }[] = [];

  /**
   * Default region used when a CSV phone number has no international prefix.
   * When set, numbers written in national format (e.g. a Ghana number that
   * starts with a leading `0`) are interpreted as belonging to this country,
   * so libphonenumber strips the national prefix and normalises them to E.164
   * (e.g. `0241234567` → `+233241234567`).
   */
  private defaultCountry?: CountryCode;

  get getDefaultCountry(): CountryCode | undefined {
    return this.defaultCountry;
  }

  get getFormattedData() {
    return this.formattedData;
  }

  get getInvalids() {
    return this.formattedData.filter((d) => d.isValid === false);
  }

  get getSubmittableData() {
    return this.formattedData.filter((d) => d.isValid !== false);
  }

  get getDuplicates() {
    return this.duplicates;
  }

  get getUpdated() {
    return this.updated;
  }

  get getRemoved() {
    return this.removed;
  }

  resetFormattedCSVRelatedInfo() {
    this.formattedData = [];
    this.duplicates = [];
    this.updated = [];
    this.removed = [];
    this.defaultCountry = undefined;
  }

  /**
   * Re-run validation against the raw CSV data using `country` as the default
   * region. Numbers in national format (commonly with a leading `0`) are then
   * re-parsed into valid E.164 numbers for that country. Pass `undefined` to
   * clear the default and revert to international-only parsing.
   *
   * Note: this reprocesses the whole dataset from the original file, so any
   * manual edits/removals made in the preview are reset.
   */
  applyDefaultCountryAndRegenerate(country?: CountryCode): void {
    this.defaultCountry = country;
    this.firstUpdateFormattedData();
  }

  /**
   * Rebuild the working list from the raw file. Edits and removals made on
   * the previous list no longer apply to it, so their records go too;
   * otherwise the review would report changes the new list does not have.
   */
  firstUpdateFormattedData(): void {
    this.formattedData = [];
    this.duplicates = [];
    this.updated = [];
    this.removed = [];

    // -----------------------------------------------------------------
    // 1. Group by E.164 (only for rows that *are* valid)
    // -----------------------------------------------------------------
    const phoneGroups = new Map<
      string,
      {
        count: number;
        variations: Set<string>;
        phoneNumber: PhoneNumber | null;
        cleanDisplay: string;
        country?: string;
      }
    >();

    // -----------------------------------------------------------------
    // 2. Build a list of *all* rows (valid + invalid) with a stable ID
    // -----------------------------------------------------------------
    interface TempRow {
      raw: string; // original value from CSV
      parsed?: PhoneNumber; // only if valid
      e164?: string; // only if valid
      display: string; // what we show in UI
      country?: string;
      isValid: boolean;
      row: number; // 1-based position among data rows
      // we will create a stable ID later
    }

    const allRows: TempRow[] = [];

    const phoneKey = this.columnMapping['phoneNumber'] || 'phoneNumber';
    for (const [index, item] of this.formattedFileData.entries()) {
      // String(): a cell can still be a number, so never call trim() on it directly.
      const raw = String(item[phoneKey] ?? '').trim();

      // Flexible parse so numbers whose leading "+" was stripped by Excel
      // (e.g. "233241234567") are still recognised as international.
      const parsed = raw
        ? parseFlexiblePhoneNumber(raw, this.defaultCountry)
        : null;
      const isValid = !!parsed;

      const temp: TempRow = {
        raw,
        parsed: isValid ? parsed! : undefined,
        e164: isValid ? parsed!.number : undefined,
        display: isValid ? parsed!.formatInternational() : raw || '(empty)',
        country: isValid ? parsed!.country : undefined,
        isValid,
        row: index + 1,
      };

      allRows.push(temp);

      // ---- still fill the grouping map for duplicate detection ----
      if (isValid && temp.e164) {
        if (!phoneGroups.has(temp.e164)) {
          phoneGroups.set(temp.e164, {
            count: 0,
            variations: new Set(),
            phoneNumber: parsed!,
            cleanDisplay: temp.display,
            country: temp.country,
          });
        }
        const group = phoneGroups.get(temp.e164)!;
        group.count++;
        group.variations.add(raw);
      }
    }

    // -----------------------------------------------------------------
    // 3. Create duplicate entries (same as before)
    // -----------------------------------------------------------------
    for (const [e164, group] of phoneGroups) {
      if (group.count > 1) {
        this.duplicates.push({
          phoneNumber: group.cleanDisplay,
          count: group.count,
          variations: Array.from(group.variations),
          country: group.country,
        });
      }
    }
    this.duplicates.sort((a, b) => b.count - a.count);

    // -----------------------------------------------------------------
    // 4. Build final `formattedData` – **every row**
    // -----------------------------------------------------------------
    // Helper: stable ID for rows that have no E.164
    let invalidCounter = 0;
    const getStableId = (row: TempRow): string => {
      if (row.e164) return row.e164; // valid → E.164
      return `invalid-${++invalidCounter}-${row.raw}`; // unique for invalids
    };

    for (const row of allRows) {
      const entry = {
        // Use the normalised E.164 number for valid rows so what we display and
        // ultimately submit is the corrected number (e.g. a Ghana `0241234567`
        // becomes `+233241234567` once a default country is selected). Invalid
        // rows keep their raw value so the user can see and fix them.
        phoneNumber: row.isValid && row.e164 ? row.e164 : row.raw,
        displayNumber: row.display,
        id: getStableId(row),
        country: row.country,
        // optional flag for the UI
        isValid: row.isValid,
        row: row.row,
      };

      // **Push EVERY entry** (valid or not)
      this.formattedData.push(entry);
    }

    // -----------------------------------------------------------------
    // 5. Log summary (unchanged)
    // -----------------------------------------------------------------
  }

  altupdateElementFromFormattedData(id: string, phoneNumber: string): void {
    const index = this.formattedData.findIndex((item) => item.id === id);

    if (index !== -1) {
      const previousPhoneNumber = this.formattedData[index].phoneNumber;

      // Update the main formattedData entry
      this.formattedData[index] = { ...this.formattedData[index], phoneNumber };
      this.formattedData = [...this.formattedData]; // trigger change detection

      // Check if this item already exists in the updated array
      const updatedIndex = this.updated.findIndex(
        (u) => u.id === previousPhoneNumber
      );

      if (updatedIndex !== -1) {
        // Update existing record in the updated array
        this.updated[updatedIndex].update = phoneNumber;
      } else {
        // Add a new record to the updated array
        this.updated.push({
          id: previousPhoneNumber,
          update: phoneNumber,
        });
      }
    } else {
    }
  }

  /**
   * Updates an entry and re-validates it. Returns `true` if the new number is valid.
   */
  updateAndRevalidateEntry(id: string, newPhoneNumber: string): boolean {
    const index = this.formattedData.findIndex((item) => item.id === id);
    if (index === -1) return false;

    const parsed = newPhoneNumber
      ? parseFlexiblePhoneNumber(newPhoneNumber, this.defaultCountry)
      : null;
    const isValid = !!parsed;
    const newId = isValid ? parsed!.number : id;
    const previous = this.formattedData[index].phoneNumber;

    this.formattedData[index] = {
      ...this.formattedData[index],
      phoneNumber: newPhoneNumber,
      id: newId,
      isValid,
      country: isValid ? parsed!.country : undefined,
    };
    this.formattedData = [...this.formattedData];

    this.recordEdit(id, newId, previous, newPhoneNumber);

    return isValid;
  }

  updateElementFromFormattedData(id: string, phoneNumber: string) {
    const index = this.formattedData.findIndex((item) => item.id === id);

    if (index !== -1) {
      const previous = this.formattedData[index].phoneNumber;
      // update the main formattedData entry
      this.formattedData[index] = { ...this.formattedData[index], phoneNumber };
      this.formattedData = [...this.formattedData]; // trigger change detection

      this.recordEdit(id, id, previous, phoneNumber);
    }
  }

  /**
   * Keep one record per edited entry, following the entry when re-validation
   * gives it a new id. A number edited again keeps the value it had before
   * the first edit, so the review shows "original → latest".
   */
  private recordEdit(id: string, newId: string, previous: string, update: string): void {
    const existing = this.updated.find((u) => u.id === id);
    if (existing) {
      existing.id = newId;
      existing.update = update;
    } else {
      this.updated.push({ id: newId, previous, update });
    }
  }

  removeElementFromFormattedData(id: string): void {
    // Find the index of the element with the given id
    const index = this.formattedData.findIndex((item) => item.id === id);

    if (index !== -1) {
      // Keep a record of the removed phone number for reference
      const removedItem = this.formattedData[index];
      this.removed.push({ phoneNumber: removedItem.phoneNumber });

      // Remove the element from the array
      this.formattedData.splice(index, 1);

      // Optional: Reassign array to trigger Angular change detection
      this.formattedData = [...this.formattedData];
    } else {
    }
  }

  /** Extra rows caused by repeats: a number listed 3 times adds 2. */
  get getRepeatedRowCount(): number {
    return this.duplicates.reduce((sum, d) => sum + Math.max(0, d.count - 1), 0);
  }

  /**
   * Save the numbers that failed validation as a CSV, with the data row each
   * came from, so they can be fixed in bulk and uploaded again.
   * Returns how many rows were written.
   */
  downloadInvalids(): number {
    const invalids = this.getInvalids;
    if (!invalids.length) return 0;
    const checkedAs = this.defaultCountry
      ? `National numbers read as ${this.defaultCountry}`
      : 'International format only';
    const csv = toCsv(invalids, [
      { label: 'Data row', value: (entry) => entry.row ?? '' },
      { label: 'Phone number as entered', value: (entry) => entry.phoneNumber },
      {
        label: 'Problem',
        value: (entry) =>
          (entry.phoneNumber ?? '').trim()
            ? 'Not a valid phone number'
            : 'Empty cell',
      },
      { label: 'Checked as', value: () => checkedAs },
    ]);
    const source = this.fileName.replace(/\.[^.]+$/, '');
    downloadCsv(csvFileName('invalid numbers', source), csv);
    return invalids.length;
  }

  get getPhoneNumberCSVFinalData(): Omit<IPhoneNumbersFromCSV, 'batchId'> {
    return {
      fileName: this.getFileName,
      fileSize: this.getFileSize,
      filePhoneNumbersCount: this.getFormattedFileDataCount,
      duplicated: this.getDuplicates,
      removed: this.getRemoved,
      submitted: this.getSubmittableData,
      updated: this.getUpdated,
    };
  }

  /** All processed CSV batches */
  private allformattedFileData: IPhoneNumbersFromCSV[] = [];

  addCurrentToAllFormatted(): void {
    const snapshot: IPhoneNumbersFromCSV = {
      batchId: uuidv4(), // unique!
      fileName: this.getFileName,
      fileSize: this.getFileSize,
      filePhoneNumbersCount: this.getFormattedFileDataCount,

      submitted: structuredClone(this.getSubmittableData),
      duplicated: structuredClone(this.getDuplicates),
      updated: structuredClone(this.getUpdated),
      removed: structuredClone(this.getRemoved),
    };

    this.allformattedFileData.push(snapshot);
    // now reset the current data
    this.resetFileRelatedInfo();
    this.resetFormattedCSVRelatedInfo();
  }

  /** Simple deep-copy for plain objects / arrays (good enough for this data shape) */
  private deepCopy<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Remove a saved batch by its **index** in `allformattedFileData`.
   *
   * @param index Index of the batch to delete (0-based).
   * @returns `true` if removed, `false` if index out of range.
   */
  removeFromAllFormattedUsingIndex(index: number): boolean {
    if (index >= 0 && index < this.allformattedFileData.length) {
      this.allformattedFileData.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Optional overload: remove by a unique identifier (e.g. fileName + timestamp)
   */
  // ─────────────────────────────────────────────────────────────────
  //  REMOVE BY UUID (most useful for UI lists)
  // ─────────────────────────────────────────────────────────────────
  removeFromAllFormattedById(batchId: string): boolean {
    const idx = this.allformattedFileData.findIndex(
      (b) => b.batchId === batchId
    );
    return this.removeFromAllFormattedUsingIndex(idx);
  }

  // ─────────────────────────────────────────────────────────────────
  //  READ-ONLY GETTER
  // ─────────────────────────────────────────────────────────────────
  get getAllFormattedFileData(): readonly IPhoneNumbersFromCSV[] {
    return this.allformattedFileData;
  }
}
